/**
 * Portfolio Work Report
 * 5 clearly labeled sections for fast management review:
 *   NYS — NOT YET STARTED
 *   T1 Progress
 *   T2 Progress
 *   T3 Progress
 *   C-P&L Complete
 *
 * Color scheme: Blue & Teal (Professional & Clean)
 * Score card validation: section counts compared against score card; discrepancies flagged.
 */

import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { Printer, RefreshCw, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { useRef as _useRef } from "react"; // kept for potential future use

const APX_LOGO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310419663029793542/5GqF8Kn9QcwXve8h8refaY/apx-logo-final_808b0f18.png";

// Blue & Teal palette — one entry per stage
const STAGE = {
  NYS: {
    header: "NYS — NOT YET STARTED",
    scoreKey: "nys" as const,
    bg: "#eff6ff", border: "#1d4ed8", text: "#1e3a8a",
    badgeBg: "#1d4ed8", rowAlt: "#dbeafe",
  },
  T1: {
    header: "T1 Progress",
    scoreKey: "t1" as const,
    bg: "#ecfeff", border: "#0891b2", text: "#164e63",
    badgeBg: "#0891b2", rowAlt: "#cffafe",
  },
  T2: {
    header: "T2 Progress",
    scoreKey: "t2" as const,
    bg: "#eff6ff", border: "#3b82f6", text: "#1e40af",
    badgeBg: "#3b82f6", rowAlt: "#dbeafe",
  },
  T3: {
    header: "T3 Progress",
    scoreKey: "t3" as const,
    bg: "#f5f3ff", border: "#7c3aed", text: "#4c1d95",
    badgeBg: "#7c3aed", rowAlt: "#ede9fe",
  },
  CPL: {
    header: "C-P&L Complete",
    scoreKey: "t3c" as const,
    bg: "#f0fdf4", border: "#16a34a", text: "#14532d",
    badgeBg: "#16a34a", rowAlt: "#dcfce7",
  },
} as const;

type StageKey = keyof typeof STAGE;

type Client = {
  id: number;
  clientName: string;
  contactPerson: string;
  clientPhone: string;
  clientEmail: string;
  bookkeeperBankFeed: string;
  bookkeeperCoding: string;
};

function groupByPair(clients: Client[]): Map<string, Client[]> {
  const map = new Map<string, Client[]>();
  for (const c of clients) {
    const key = `${c.bookkeeperBankFeed}|||${c.bookkeeperCoding}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(c);
  }
  return map;
}

function currentMonthLabel() {
  return new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
function priorMonthEnd() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 0)
    .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

const thStyle: React.CSSProperties = {
  padding: "0.4rem 0.75rem",
  textAlign: "left",
  fontSize: "0.8rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#4b5563",
  borderBottom: "1px solid #d1d5db",
  borderRight: "1px solid #e5e7eb",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdStyle: React.CSSProperties = {
  padding: "0.45rem 0.75rem",
  borderBottom: "1px solid #f0f0f0",
  borderRight: "1px solid #f0f0f0",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: "0.875rem",
};

// ── Single stage section ──────────────────────────────────────────────────────
function StageSection({
  stageKey,
  clients,
  scoreCardCount,
  globalOffset,
}: {
  stageKey: StageKey;
  clients: Client[];
  scoreCardCount: number;
  globalOffset: number;
}) {
  const s = STAGE[stageKey];
  const hasDisc = clients.length !== scoreCardCount;
    return (
    <section
      className="stage-section"
      style={{
        borderLeft: `4px solid ${hasDisc ? "#ef4444" : s.border}`,
        marginBottom: "1.5rem",
        outline: hasDisc ? "2px solid #fca5a5" : "none",
        borderRadius: "0 4px 4px 0",
      }}
    >
      {/* Section header — plain static header, no sticky */}
      <div
        className="stage-header"
        style={{
          background: hasDisc ? "#fff1f2" : s.bg,
          borderBottom: `2px solid ${hasDisc ? "#ef4444" : s.border}`,
          padding: "0.55rem 1rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <span style={{
          fontWeight: 800,
          fontSize: "1rem",
          color: hasDisc ? "#b91c1c" : s.text,
          letterSpacing: "0.01em",
        }}>
          {s.header}
        </span>

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.6rem" }}>
          {/* Count badge */}
          <span style={{
            background: hasDisc ? "#ef4444" : s.badgeBg,
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.78rem",
            padding: "0.15rem 0.6rem",
            borderRadius: "12px",
            fontFamily: "monospace",
          }}>
            {clients.length}
          </span>
          {/* Score card match indicator */}
          {hasDisc ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "0.68rem", color: "#b91c1c", fontWeight: 600 }}>
              <AlertTriangle size={11} /> card: {scoreCardCount}
            </span>
          ) : (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "0.68rem", color: "#15803d" }}>
              <CheckCircle2 size={11} />
            </span>
          )}
        </span>
      </div>

      {/* Discrepancy banner */}
      {hasDisc && (
        <div style={{ background: "#fef2f2", borderBottom: "1px solid #fca5a5", padding: "0.35rem 1rem", display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.73rem", color: "#b91c1c", fontWeight: 600 }}>
          <AlertTriangle size={12} />
          DISCREPANCY — Score card: {scoreCardCount} · This section: {clients.length} · Diff: {clients.length - scoreCardCount > 0 ? "+" : ""}{clients.length - scoreCardCount}
        </div>
      )}

      {clients.length === 0 ? (
        <div style={{ padding: "0.65rem 1rem", fontSize: "0.8rem", color: "#94a3b8", background: "#fafafa", fontStyle: "italic" }}>
          No clients in this stage.
        </div>
      ) : (
                <>
          <table className="report-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "3%", textAlign: "center", padding: "0.4rem 0.2rem" }}>#</th>
                <th style={{ ...thStyle, width: "28%" }}>Client Name</th>
                <th style={{ ...thStyle, width: "8%" }}>Bkkpr Assigned</th>
                <th style={{ ...thStyle, width: "16%" }}>Contact</th>
                <th style={{ ...thStyle, width: "14%" }}>Phone</th>
                <th style={{ ...thStyle, width: "14%", borderRight: "none" }}>Email</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c, idx) => (
                <tr key={c.id} style={{ background: idx % 2 === 1 ? s.rowAlt : "#ffffff" }}>
                  <td style={{ ...tdStyle, textAlign: "center", color: "#94a3b8", padding: "0.45rem 0.2rem" }}>{globalOffset + idx + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: "#1e293b" }}>{c.clientName}</td>
                  <td style={{ ...tdStyle, color: "#374151", fontWeight: 500 }}>{c.bookkeeperBankFeed || "Bkkpr Manager"}</td>
                  <td style={{ ...tdStyle, color: "#374151" }}>{c.contactPerson || "—"}</td>
                  <td style={{ ...tdStyle, color: "#374151" }}>{c.clientPhone || "—"}</td>
                  <td style={{ ...tdStyle, color: "#374151", borderRight: "none" }}>{c.clientEmail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Section total footer */}
          <div style={{
            textAlign: "right",
            padding: "0.3rem 1rem",
            fontSize: "0.72rem",
            fontWeight: 600,
            color: hasDisc ? "#b91c1c" : s.text,
            background: hasDisc ? "#fff1f2" : s.bg,
            borderTop: `1px solid ${hasDisc ? "#ef4444" : s.border}`,
          }}>
            Total: {clients.length}
          </div>
        </>
      )}
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PortfolioWorkReport() {
  const { data, isLoading, error, refetch } = trpc.tierTasks.portfolioReport.useQuery();

  // No floating bar — removed per user request

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: "#bfdbfe", borderTopColor: "#1d4ed8" }} />
          <span className="ml-3 text-sm text-gray-500">Loading report…</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-red-500 text-sm">
          Failed to load report data. Please try again.
        </div>
      </DashboardLayout>
    );
  }

  const { scoreCard, nysClients, t1Clients, t2Clients, t3Clients, cplClients, byBookkeeper, sectionCounts, biAnnualActive } = data;

  const totalClients = nysClients.length + t1Clients.length + t2Clients.length + t3Clients.length + cplClients.length;

  const discrepancies = [
    { key: "NYS",   section: sectionCounts.nys,  card: scoreCard.nys },
    { key: "T1",    section: sectionCounts.t1,   card: scoreCard.t1 },
    { key: "T2",    section: sectionCounts.t2,   card: scoreCard.t2 },
    { key: "T3",    section: sectionCounts.t3,   card: scoreCard.t3 },
    { key: "C-P&L", section: sectionCounts.cpl,  card: scoreCard.t3c },
    { key: "TOTAL", section: sectionCounts.total, card: scoreCard.total },
  ].filter(d => d.section !== d.card);

  const hasAnyDiscrepancy = discrepancies.length > 0;

  // NYS by bookkeeper summary
  type BkRow = { bookkeeper: string; bankFeed: number; coding: number };
  const bkMap = new Map<string, BkRow>();
  for (const row of byBookkeeper) {
    if (!bkMap.has(row.bookkeeper)) bkMap.set(row.bookkeeper, { bookkeeper: row.bookkeeper, bankFeed: 0, coding: 0 });
    const e = bkMap.get(row.bookkeeper)!;
    if (row.role === "Bank Feed") e.bankFeed = row.count;
    else e.coding = row.count;
  }
  const bkSummary = Array.from(bkMap.values()).sort((a, b) => a.bookkeeper.localeCompare(b.bookkeeper));

  // Cumulative offsets for row numbering across sections
  const offsets = {
    nys: 0,
    t1: nysClients.length,
    t2: nysClients.length + t1Clients.length,
    t3: nysClients.length + t1Clients.length + t2Clients.length,
    cpl: nysClients.length + t1Clients.length + t2Clients.length + t3Clients.length,
  };

  return (
    <DashboardLayout>


      {/* ── Printable body ── */}
      <div id="portfolio-report-body" style={{ background: "#ffffff", padding: "1.5rem 2rem", maxWidth: "1100px", margin: "0 auto" }}>

        {/* Report header */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
          <img src={APX_LOGO} alt="APX" style={{ width: "44px", height: "44px", borderRadius: "50%", objectFit: "cover" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.18em", color: "#94a3b8", textTransform: "uppercase" }}>APX ADVISORS — CONFIDENTIAL</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#1e3a8a", lineHeight: 1.1, fontFamily: "Georgia, serif" }}>Portfolio Work Report</div>
            <div style={{ fontSize: "0.73rem", color: "#64748b" }}>
              {currentMonthLabel()} · Core Clients Only · As of {priorMonthEnd()}
              {biAnnualActive && <span style={{ marginLeft: "0.5rem", color: "#7c3aed", fontWeight: 600 }}> · Bi-Annual Clients Included</span>}
            </div>
          </div>
          {/* Refresh + Print buttons — right of header, screen only */}
          <div className="no-print" style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            <button onClick={() => refetch()} style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.38rem 0.85rem", borderRadius: "6px", fontSize: "0.78rem", fontWeight: 500, background: "#f1f5f9", color: "#374151", border: "1px solid #cbd5e1", cursor: "pointer" }}>
              <RefreshCw size={12} /> Refresh
            </button>
            <button onClick={() => window.print()} style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.38rem 0.95rem", borderRadius: "6px", fontSize: "0.78rem", fontWeight: 600, background: "#1d4ed8", color: "#ffffff", border: "none", cursor: "pointer" }}>
              <Printer size={12} /> Print / PDF
            </button>
          </div>
        </div>
        <div style={{ height: "3px", background: "linear-gradient(to right, #1d4ed8, #0891b2, #3b82f6, #7c3aed, #16a34a)", borderRadius: "2px", marginBottom: "1.25rem" }} />

        {/* Global discrepancy alert */}
        {hasAnyDiscrepancy && (
          <div style={{ background: "#fef2f2", border: "2px solid #ef4444", borderRadius: "6px", padding: "0.65rem 1rem", marginBottom: "1.25rem", display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
            <AlertTriangle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: "1px" }} />
            <div>
              <div style={{ fontWeight: 700, color: "#b91c1c", fontSize: "0.82rem", marginBottom: "0.25rem" }}>Score Card Discrepancy</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                {discrepancies.map(d => (
                  <span key={d.key} style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "1px 7px", fontSize: "0.7rem", fontWeight: 700, color: "#b91c1c" }}>
                    {d.key}: report {d.section} · card {d.card} ({d.section - d.card > 0 ? "+" : ""}{d.section - d.card})
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}



        {/* Score Card — static in page flow */}
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.5rem" }}>Monthly Progress Score Card</div>
          <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
            {([
              { key: "NYS",   val: scoreCard.nys,  sec: sectionCounts.nys,  s: STAGE.NYS },
              { key: "T1",    val: scoreCard.t1,   sec: sectionCounts.t1,   s: STAGE.T1 },
              { key: "T2",    val: scoreCard.t2,   sec: sectionCounts.t2,   s: STAGE.T2 },
              { key: "T3",    val: scoreCard.t3,   sec: sectionCounts.t3,   s: STAGE.T3 },
              { key: "C-P&L", val: scoreCard.t3c,  sec: sectionCounts.cpl,  s: STAGE.CPL },
            ] as const).map(({ key, val, sec, s }) => {
              const mm = val !== sec;
              return (
                <div key={key} style={{ flex: "1", minWidth: "85px", background: mm ? "#fff1f2" : s.bg, border: `2px solid ${mm ? "#ef4444" : s.border}`, borderRadius: "10px", padding: "0.6rem 0.7rem", textAlign: "center", position: "relative", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                  {mm && <span style={{ position: "absolute", top: "4px", right: "4px" }}><AlertTriangle size={10} color="#ef4444" /></span>}
                  <div style={{ fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: mm ? "#b91c1c" : s.text, marginBottom: "0.1rem" }}>{key}</div>
                  <div style={{ fontSize: "2rem", fontWeight: 800, color: mm ? "#ef4444" : s.badgeBg, lineHeight: 1, fontFamily: "Georgia, serif" }}>{val}</div>
                  {mm && <div style={{ fontSize: "0.58rem", color: "#b91c1c", marginTop: "1px" }}>report: {sec}</div>}
                </div>
              );
            })}
            <div style={{ flex: "1", minWidth: "85px", background: sectionCounts.total !== scoreCard.total ? "#fff1f2" : "#f8fafc", border: `2px solid ${sectionCounts.total !== scoreCard.total ? "#ef4444" : "#cbd5e1"}`, borderRadius: "10px", padding: "0.6rem 0.7rem", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: "0.1rem" }}>TOTAL</div>
              <div style={{ fontSize: "2rem", fontWeight: 800, color: "#334155", lineHeight: 1, fontFamily: "Georgia, serif" }}>{scoreCard.total}</div>
              {sectionCounts.total !== scoreCard.total && <div style={{ fontSize: "0.58rem", color: "#b91c1c", marginTop: "1px" }}>report: {sectionCounts.total}</div>}
            </div>
          </div>
        </div>



        {/* Bi-annual note */}
        {biAnnualActive && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: "6px", padding: "0.45rem 0.75rem", fontSize: "0.73rem", color: "#5b21b6", marginBottom: "1.1rem" }}>
            <Info size={12} />
            <strong>Bi-Annual Active Month</strong> — {new Date().toLocaleDateString("en-US", { month: "long" })} is a bi-annual processing month. Bi-annual clients are included in this report.
          </div>
        )}

        <div style={{ borderTop: "2px solid #e2e8f0", marginBottom: "1.25rem" }} />

        {/* ── 5 Stage Sections ── */}
        <StageSection stageKey="NYS" clients={nysClients} scoreCardCount={scoreCard.nys}  globalOffset={offsets.nys} />
        <StageSection stageKey="T1"  clients={t1Clients}  scoreCardCount={scoreCard.t1}   globalOffset={offsets.t1} />
        <StageSection stageKey="T2"  clients={t2Clients}  scoreCardCount={scoreCard.t2}   globalOffset={offsets.t2} />
        <StageSection stageKey="T3"  clients={t3Clients}  scoreCardCount={scoreCard.t3}   globalOffset={offsets.t3} />
        <StageSection stageKey="CPL" clients={cplClients} scoreCardCount={scoreCard.t3c}  globalOffset={offsets.cpl} />

        {/* Grand total */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.45rem 1rem", fontSize: "0.85rem", fontWeight: 700, color: "#1e3a8a", background: "#eff6ff", border: `2px solid ${hasAnyDiscrepancy ? "#ef4444" : "#1d4ed8"}`, borderRadius: "6px", marginBottom: "1.5rem" }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 400, color: "#64748b" }}>Score Card Total: <strong>{scoreCard.total}</strong></span>
          <span>
            TOTAL CORE CLIENTS: {totalClients}
            {totalClients !== scoreCard.total && (
              <span style={{ marginLeft: "0.5rem", color: "#ef4444", fontSize: "0.72rem" }}>
                <AlertTriangle size={11} style={{ display: "inline", marginRight: "3px" }} />MISMATCH
              </span>
            )}
          </span>
        </div>

        <div style={{ borderTop: "2px solid #e2e8f0", marginBottom: "1.1rem" }} />

        {/* NYS by Bookkeeper */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.45rem" }}>NYS Count by Bookkeeper</div>
          {bkSummary.length === 0 ? (
            <p style={{ fontSize: "0.78rem", color: "#94a3b8", fontStyle: "italic" }}>No NYS data.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
              <thead>
                <tr style={{ background: "#eff6ff" }}>
                  <th style={{ ...thStyle, borderLeft: "3px solid #1d4ed8" }}>Bookkeeper</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Bank Feed</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Coding</th>
                  <th style={{ ...thStyle, textAlign: "center", borderRight: "none" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {bkSummary.map((row, idx) => (
                  <tr key={row.bookkeeper} style={{ background: idx % 2 === 1 ? "#dbeafe" : "#ffffff" }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#1e293b", borderLeft: "3px solid #1d4ed8" }}>{row.bookkeeper}</td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "#374151" }}>{row.bankFeed || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "#374151" }}>{row.coding || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 700, color: "#1e3a8a", borderRight: "none" }}>{row.bankFeed + row.coding}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.62rem", color: "#94a3b8", borderTop: "1px solid #e2e8f0", paddingTop: "0.45rem" }}>
          <span>APX ADVISORS — Confidential · Internal Use Only</span>
          <span>Generated {new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: portrait; margin: 1.5cm 1.2cm 1.8cm 1.2cm; }
          body * { visibility: hidden; }
          #portfolio-report-body, #portfolio-report-body * { visibility: visible; }
          #portfolio-report-body {
            position: fixed !important; top: 0 !important; left: 0 !important;
            width: 100% !important; padding: 0 !important; margin: 0 !important;
            max-width: 100% !important; background: white !important;
          }
          .no-print { display: none !important; }
          .no-print-sticky { position: static !important; box-shadow: none !important; top: auto !important; }
          .no-print-sticky-scorecard { position: static !important; box-shadow: none !important; top: auto !important; margin-left: 0 !important; margin-right: 0 !important; padding-left: 0 !important; padding-right: 0 !important; border-bottom: none !important; }
          .bk-group-block { page-break-inside: avoid; }
          .stage-section { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
          .stage-header { page-break-after: avoid; }
          .bk-pair-header { page-break-after: avoid; }
        }
      `}</style>
    </DashboardLayout>
  );
}
